import { post, get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import Logger from '@/logger';
import awsmobile from './aws-exports';
import { mentionsLiveDemo } from '@/liveDemo/flag';

const logger = new Logger({ screen: 'RestApiFallback' });

/**
 * Validates that Amplify REST config is available and logs diagnostics if not.
 * Called at app startup.
 */
export function validateRestConfig() {
    try {
        const config = Amplify.getConfig();
        const restKeys = Object.keys(config?.API?.REST || {});
        if (restKeys.length === 0) {
            logger.error('REST config is EMPTY in Amplify.getConfig(). Fallback will be used for all REST calls.', {
                apiConfig: JSON.stringify(config?.API || {}),
            });
        } else {
            logger.console(`REST config OK: [${restKeys.join(', ')}]`);
        }
    } catch (error) {
        logger.error('Failed to read Amplify config', { error });
    }
}

/**
 * One shape for a failed REST call, whichever path made it.
 *  - the native fallback throws an Error with `.status` and `.body` (text);
 *  - Amplify throws a RestApiError with `response.statusCode` and `response.body` (text);
 *  - an older fallback error carries both only inside "REST fallback error 400: {...}".
 * `body` is the parsed JSON when it parses and the text otherwise; `code` is the body's string
 * `code` field (the backend's machine-readable error), or null.
 *
 * @returns {{status: number|null, body: object|string|null, code: string|null}}
 */
export function parseRestError(error) {
    const response = error?.response;
    let status = Number.isFinite(error?.status) ? error.status
        : (Number.isFinite(response?.statusCode) ? response.statusCode : null);
    let raw = error?.body !== undefined ? error.body : response?.body;
    if (typeof error?.message === 'string') {
        const legacy = /^REST fallback error (\d{3}): ([\s\S]*)$/.exec(error.message);
        if (legacy) {
            if (status == null) status = Number(legacy[1]);
            if (raw === undefined) raw = legacy[2];
        }
    }
    let body = raw === undefined ? null : raw;
    if (typeof body === 'string') {
        try { body = JSON.parse(body); } catch (_) { /* not JSON: keep the text */ }
    }
    const code = body && typeof body === 'object' && typeof body.code === 'string' ? body.code : null;
    return { status, body, code };
}

/**
 * Performs a REST POST using Amplify, falling back to native fetch if Amplify
 * throws InvalidApiName.
 */
export async function restPost({ apiName, path, options }) {
    // Live sales demo: a request that names a demo order is answered locally and never leaves the tablet.
    // The single choke point of every REST call, so nothing can slip past it.
    if (mentionsLiveDemo({ path, options })) {
        const { demoRestPost } = await import('@/liveDemo/live');
        return demoRestPost({ apiName, path, options });
    }
    try {
        const restOperation = post({ apiName, path, options });
        const { body } = await restOperation.response;
        return await body.json();
    } catch (error) {
        if (error?.name === 'InvalidApiName') {
            logger.warning(`Amplify InvalidApiName for POST "${apiName}${path}". Falling back to native fetch.`);
            return await nativeFetch('POST', apiName, path, options);
        }
        throw error;
    }
}

/**
 * Performs a REST GET using Amplify, falling back to native fetch if Amplify
 * throws InvalidApiName.
 */
export async function restGet({ apiName, path, options }) {
    if (mentionsLiveDemo({ path, options })) {
        const { demoRestGet } = await import('@/liveDemo/live');
        return demoRestGet({ apiName, path, options });
    }
    try {
        const restOperation = get({ apiName, path, options });
        const { body } = await restOperation.response;
        return await body.json();
    } catch (error) {
        if (error?.name === 'InvalidApiName') {
            logger.warning(`Amplify InvalidApiName for GET "${apiName}${path}". Falling back to native fetch.`);
            return await nativeFetch('GET', apiName, path, options);
        }
        throw error;
    }
}

/**
 * Native fetch fallback that replicates what Amplify REST does:
 * resolves the endpoint from aws-exports and attaches the Cognito auth token.
 */
async function nativeFetch(method, apiName, path, options) {
    const endpoint = awsmobile.API?.REST?.[apiName]?.endpoint;
    if (!endpoint) {
        throw new Error(`No endpoint found for REST API "${apiName}" in aws-exports`);
    }

    const url = `${endpoint}${path}`;
    const session = await fetchAuthSession();
    const token = session?.tokens?.idToken?.toString();

    const headers = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = token;
    }

    logger.console(`Native fetch ${method} ${url}`);
    const fetchOptions = { method, headers };
    if (method === 'POST' && options?.body) {
        fetchOptions.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
        const errorText = await response.text();
        // The message format is unchanged (log readers and old call sites match on it), but the
        // status and the raw body now travel as fields too, so a caller reacts to WHAT the server
        // said instead of string-matching an exception. See parseRestError.
        const failure = new Error(`REST fallback error ${response.status}: ${errorText}`);
        failure.status = response.status;
        failure.body = errorText;
        throw failure;
    }

    return await response.json();
}
